// FILE: src/components/common/DateInput.tsx

import React, { useRef } from 'react';
import cn from 'classnames';
import dayjs from 'dayjs';
import { FiCalendar } from 'react-icons/fi';

export interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> {
  value: string; // Expects value in 'YYYY-MM-DD' format
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  compact?: boolean;
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, value, onChange, name, id, disabled, compact = false, ...props }, ref) => {
    const internalInputRef = useRef<HTMLInputElement>(null);

    const setRefs = (element: HTMLInputElement | null) => {
      (internalInputRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
      }
    };

    const handleDisplayClick = () => {
      if (!disabled) {
        internalInputRef.current?.showPicker();
      }
    };

    const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '';

    return (
      <div className={cn('relative w-full', className)}>
        <input
          type="date"
          ref={setRefs}
          name={name}
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
          {...props}
        />

        <div
          className={cn(
            'flex items-center justify-between w-full border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800',
            compact 
              ? 'h-[30px] rounded px-2 py-1 text-xs focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent' 
              : 'h-10 rounded-lg px-3 py-2 text-sm ring-offset-white dark:ring-offset-gray-900 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
            'text-gray-900 dark:text-white',
            { 'cursor-not-allowed opacity-50': disabled },
            { 'cursor-pointer': !disabled }
          )}
          aria-hidden="true"
          onClick={handleDisplayClick}
        >
          <span className={cn(
            compact ? 'text-xs' : 'text-sm',
            { 'text-slate-400 dark:text-slate-500': !displayValue }
          )}>
            {/* ============================================= */}
            {/* THIS IS THE ONLY LINE THAT HAS BEEN CHANGED */}
            {/* ============================================= */}
            {displayValue || 'jj / mm / aaaa'}
          </span>
          <FiCalendar className={cn(
            compact ? 'h-3 w-3' : 'h-4 w-4',
            'text-slate-400 dark:text-gray-400'
          )} />
        </div>
      </div>
    );
  }
);

export { DateInput };